import { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useAuth } from "context/AuthContext";
import SolarLogoutOutline from "../icons/SolarLogoutOutline";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SvgLock from "../svg/svgLock";
import * as apiProfile from "api/apiProfile";
import ThemeLink from "./ThemeLink";
import useSetTelegram from "./hooks/useSetTelegram";
import { useToast } from "context/ToastContext";
import { deleteCloudflareFiles, getCloudflareUploadUrls, linkCloudflareFiles } from "api/apiCloudflare";
import { toStoredCloudFile } from 'utils/helpers/files/cloudFiles';
import { uploadFileWithProgress } from "utils/helpers/featchFiles/uploadFileWithProgress";
import { fileToWebpFile } from "utils/helpers/imgFunctions/fileToWebpFile";

const AVATAR_WEBP_QUALITY = 0.8;
const AVATAR_MAX_SIZE = 512;

const UserDropDown = () => {
    const { auth, LogOut, updateAuthUser } = useAuth();
    const [imgUser, setImgUser] = useState(`${auth.user.userPicture}?v=${Date.now()}`);
    const [isUploading, setIsUploading] = useState(false);
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { openSetTelegram } = useSetTelegram();
    const refInputAvatar = useRef(null);

    const uploadAvatar = async (userId, file) => {
        const optimizedFile = await fileToWebpFile(file, AVATAR_WEBP_QUALITY, AVATAR_MAX_SIZE, AVATAR_MAX_SIZE);
        const filesMeta = [
            {
                fileName: optimizedFile.name,
                contentType: optimizedFile.type || "application/octet-stream",
                fileSize: optimizedFile.size,
            },
        ];
        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, { avatarId: Number(userId || 0) });
        if (!uploadConfigs?.length) return null;
        const uploaded = await uploadFileWithProgress(optimizedFile, uploadConfigs[0]);
        if (!uploaded.success) return null;
        const storedFile = toStoredCloudFile(uploaded.config);
        const linked = await linkCloudflareFiles([storedFile], { avatarId: Number(userId || 0) });
        if (!linked) return null;
        return storedFile;
    };

    const handleClickUserImg = () => {
        if (isUploading || !refInputAvatar.current) return;
        refInputAvatar.current.value = "";
        refInputAvatar.current.click();
    };

    const handleChangeAvatar = async (e) => {
        const file = e.currentTarget?.files?.[0];
        if (!file || isUploading) return;

        const userId = Number(auth.user.ID || 0);
        const oldPicture = auth.user.userPicture || "";
        const previewUrl = URL.createObjectURL(file);
        setImgUser(previewUrl);
        setIsUploading(true);

        try {
            const uploadedAvatar = await uploadAvatar(userId, file);
            if (!uploadedAvatar?.publicUrl) {
                showToast(t("common:messError"), "danger");
                setImgUser(oldPicture ? `${oldPicture}?v=${Date.now()}` : "/imgs/user.png");
                return;
            }

            const urlAvatar = await apiProfile.setAvatar(uploadedAvatar.publicUrl);
            if (!urlAvatar) {
                showToast(t("common:messError"), "danger");
                setImgUser(oldPicture ? `${oldPicture}?v=${Date.now()}` : "/imgs/user.png");
                return;
            }

            if (oldPicture && oldPicture !== urlAvatar) {
                try {
                    await deleteCloudflareFiles({
                        publicUrls: [oldPicture],
                        scope: { avatarId: userId },
                    });
                } catch {
                    // Не блокуємо оновлення аватара, якщо старий файл не видалився
                }
            }

            updateAuthUser({ userPicture: urlAvatar });
            setImgUser(`${urlAvatar}?v=${Date.now()}`);
        } finally {
            URL.revokeObjectURL(previewUrl);
            setIsUploading(false);
            if (refInputAvatar.current) refInputAvatar.current.value = "";
        }
    };

    useEffect(() => {
        setImgUser(`${auth.user.userPicture}?v=${Date.now()}`);
    }, [auth.user.userPicture]);

    return (
        <Dropdown className="pc-h-item header-user-profile">
            <input
                ref={refInputAvatar}
                type="file"
                accept=".png,.jpeg,.jpg,.webp,.gif,.svg"
                className="d-none"
                onChange={handleChangeAvatar}
            />
            <Dropdown.Toggle className="pc-head-link arrow-none me-0 border-none border-0">
                 
                <img
                    src={imgUser}
                    onError={() => setImgUser("/imgs/user.png")}
                    className={`user-avtar${isUploading ? " opacity-50" : ""}`}
                />
            </Dropdown.Toggle>

            <Dropdown.Menu className="dropdown-menu dropdown-user-profile dropdown-menu-end pc-h-dropdown">
                <div className="dropdown-header">
                    <h5 className="m-0">{t("UserDropDown.profile")}</h5>
                </div>
                <div className="dropdown-body">
                    <div className="d-flex mb-1">
                        <div className="flex-shrink-0">
                            <img
                                src={imgUser}
                                onClick={handleClickUserImg}
                                onError={() => setImgUser("/imgs/user.png")}
                                className={`user-avtar wid-35 cursor-pointer${isUploading ? " opacity-50" : ""}`}
                            />
                        </div>
                        <div className="flex-grow-1 ms-3">
                            <h6 className="mb-1">{auth.user.userName} 🖖</h6>
                            <span>{auth.user.userEmail}</span>
                        </div>
                    </div>
                    <hr className="border-secondary border-opacity-50" />

                    <ThemeLink />

                    <Link to="/recovery" className="dropdown-item">
                        <div>
                            <SvgLock />
                            <span>{t("UserDropDown.changePassword")}</span>
                        </div>
                    </Link>

                    <hr className="border-secondary border-opacity-50" />

                    <span className="dropdown-item cursor-pointer" onClick={openSetTelegram}>
                        <div className="d-flex align-items-start w-100 text-start">
                            <i className="bi bi-telegram me-2 fs-14 flex-shrink-0 mt-1" />
                            <div className="flex-grow-1 min-w-0">
                                <div>{t("UserDropDown.telegram")}</div>
                                <small className="text-muted">{t("UserDropDown.telegramMenuDesc")}</small>
                            </div>
                        </div>
                    </span>

                    <div className="d-grid mb-3 mt-5">
                        <button className="btn btn-primary" onClick={() => LogOut()}>
                            <SolarLogoutOutline className="me-2" />
                            {t("UserDropDown.logout")}
                        </button>
                    </div>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default UserDropDown;
